import { useState, useEffect } from 'react';
import { Box, Button, TextField, IconButton, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { GripVertical, Plus, Trash2, Save } from 'lucide-react';
import { usePicklist, useUpdatePicklistItems } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import type { PicklistItem } from '@lingaai/supabase-client';
import { useToast } from '../../contexts/ToastContext';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface PicklistManagerFormProps {
  picklistName: string;
  onSuccess?: () => void;
}

export function PicklistManagerForm({ picklistName, onSuccess }: PicklistManagerFormProps) {
  const { data: picklist, isLoading: picklistLoading } = usePicklist(picklistName);
  const { data: currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const updatePicklistItems = useUpdatePicklistItems();

  const [items, setItems] = useState<PicklistItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemId, setNewItemId] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  useEffect(() => {
    if (picklist?.items) {
      setItems([...picklist.items]);
    }
  }, [picklist]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    if (!draggedItem) return;
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);

    // Update order values
    const reorderedItems = newItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    setItems(reorderedItems);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleLabelChange = (id: string, newLabel: string) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, label: newLabel } : item)));
    setHasChanges(true);
  };

  const handleAddItem = () => {
    if (!newItemLabel.trim() || !newItemId.trim()) {
      showToast('Item ID and label are required', 'error');
      return;
    }

    if (items.some((item) => item.id === newItemId)) {
      showToast('Item ID already exists', 'error');
      return;
    }

    const maxOrder = Math.max(...items.map((i) => i.order || 0), 0);
    const newItem: PicklistItem = {
      id: newItemId,
      label: newItemLabel,
      order: maxOrder + 1,
    };

    setItems([...items, newItem]);
    setNewItemId('');
    setNewItemLabel('');
    setHasChanges(true);
  };

  const handleDeleteItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!picklist) return;

    try {
      await updatePicklistItems.mutateAsync({
        name: picklistName,
        items,
      });
      showToast('Picklist updated successfully', 'success');
      setHasChanges(false);
      onSuccess?.();
    } catch (error: any) {
      showToast(`Failed to update picklist: ${error.message}`, 'error');
    }
  };

  if (picklistLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!picklist) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Picklist "{picklistName}" not found
      </Alert>
    );
  }

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Admin access required to manage picklists
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Manage Picklist: {picklistName}
      </Typography>

      {/* Items List */}
      <Box sx={{ mb: 3 }}>
        {items.map((item, index) => (
          <Box
            key={item.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              mb: 1,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <GripVertical size={ICON_SIZES.md} strokeWidth={ICON_STROKE} style={{ color: '#999' }} />

            <Box sx={{ flex: 1 }}>
              {editingId === item.id ? (
                <TextField fullWidth size="small" value={item.label} onChange={(e) => handleLabelChange(item.id, e.target.value)} onBlur={() => setEditingId(null)} autoFocus />
              ) : (
                <Box onClick={() => setEditingId(item.id)} sx={{ cursor: 'text' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {item.id} | Order: {item.order}
                  </Typography>
                </Box>
              )}
            </Box>

            <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error">
              <Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* Add New Item */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add New Item
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Item ID (e.g., new_stage)" value={newItemId} onChange={(e) => setNewItemId(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Label (e.g., New Stage)" value={newItemLabel} onChange={(e) => setNewItemLabel(e.target.value)} sx={{ flex: 2 }} />
          <Button variant="outlined" startIcon={<Plus size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleAddItem}>
            Add
          </Button>
        </Box>
      </Box>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="contained" startIcon={<Save size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleSave} disabled={!hasChanges || updatePicklistItems.isPending}>
          {updatePicklistItems.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Paper>
  );
}
